import type { Entity, EntityOrElement, Persisted } from '@tunasong/schemas'
import type { Path } from 'slate'
import type { TunaPlugin } from '../plugin-types.js'

export interface RenderEntityProps<T extends Entity = Persisted<Entity>> {
  entity: T
  plugins: TunaPlugin<EntityOrElement>[]
  contentView?: string
  useDefault?: boolean
}

export interface RenderContentProps<T extends EntityOrElement> {
  element: T
  plugins: TunaPlugin<EntityOrElement>[]
  contentView?: string
  useDefault?: boolean
  elementPath?: Path | null
}

/** Cache plugin lookup by element type for O(1) content resolution */
const contentPluginsByTypeCache = new WeakMap<TunaPlugin[], Map<string, TunaPlugin>>()

const getContentPlugin = (plugins: TunaPlugin[], elementType: string): TunaPlugin | undefined => {
  let typeMap = contentPluginsByTypeCache.get(plugins)
  if (!typeMap) {
    typeMap = new Map()
    for (const p of plugins) {
      if (p.node?.type && p.components?.Content) {
        typeMap.set(p.node.type, p)
      }
    }
    contentPluginsByTypeCache.set(plugins, typeMap)
  }
  return typeMap.get(elementType)
}

export function renderContent<T extends EntityOrElement>({
  element,
  plugins,
  contentView,
  useDefault = true,
  elementPath,
}: RenderContentProps<T>) {
  const p = getContentPlugin(plugins, element.type)
  if (!p) {
    return []
  }

  const Content = p.components!.Content!
  const key = `plugin-${p.key}-${element.id}`

  if (typeof Content === 'function') {
    return [<Content key={key} element={element} plugins={plugins} elementPath={elementPath} />]
  }

  const contentComponents = Content
  const contentViewNames = Object.keys(contentComponents)
  const defaultContentView = contentViewNames[0]
  if (!defaultContentView) {
    return []
  }

  if (contentView && !contentViewNames.includes(contentView) && !useDefault) {
    return []
  }

  const contentViewWithDefault = contentView ?? element.options?.activeView ?? 'default'
  const activeContentView = contentViewNames.includes(contentViewWithDefault)
    ? contentViewWithDefault
    : defaultContentView

  const RenderContent = contentComponents[activeContentView]
  return [<RenderContent key={key} element={element} plugins={plugins} elementPath={elementPath} />]
}
